var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { documentToReactComponents, } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import TabArrow from '@hellodarwin/icons/dist/icons/TabArrow';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import stringToCamelCase from '../../features/helpers/string-to-camel-case';
import theme from '../../theme';
import Button from '../common/button';
import Div from '../common/div';
import HdTag from '../common/hd-tag';
import Typography from '../common/typography';
import PlanTileListItem from './components/list-item';
import PricingLine from './components/pricing-line';
import PlanTileToggle from './components/toggle';
import { getPlanTheme, PlanTileBody, PlanTileCard, PlanTileCta, PlanTileHeader, PlanTileWrapper, } from './styles';
const options = {
    renderNode: {
        [BLOCKS.LIST_ITEM]: (node, children) => (_jsx(PlanTileListItem, { children: children })),
        [BLOCKS.UL_LIST]: (node, children) => (_jsx(Div, { flex: "column", gap: theme.spacing.sm, children: children })),
    },
};
const horizontalOptions = {
    renderNode: {
        [BLOCKS.LIST_ITEM]: (node, children) => (_jsx(PlanTileListItem, { children: _jsx(Typography, { elementTheme: "body1", children: children }) })),
        [BLOCKS.UL_LIST]: (node, children) => (_jsx(Div, { flex: "column", gap: theme.spacing.md, style: { marginBlockStart: theme.spacing.lg }, children: children })),
    },
};
const PlanTile = (_a) => {
    var { handleClick, tileType = 'standard', isOnboarding = false, isCurrentPlan, badge } = _a, props = __rest(_a, ["handleClick", "tileType", "isOnboarding", "isCurrentPlan", "badge"]);
    const theme = useTheme();
    const { t } = useTranslation();
    const { title: monthlyPrice, yearlyPrice, type: planTitle, tag, duration: defaultDuration, shortDescription, longDescription, longDescriptionSmall, highlighted, planType, pricings, } = props;
    const [duration, setDuration] = useState(defaultDuration !== null && defaultDuration !== void 0 ? defaultDuration : 'monthly');
    const [isCollapseOpen, setIsCollapseOpen] = useState(false);
    const planTheme = useMemo(() => getPlanTheme(planType !== null && planType !== void 0 ? planType : 'free', theme), [planType, theme]);
    const formattedDescription = useMemo(() => {
        const description = tileType === 'small' || tileType === 'collapse'
            ? longDescriptionSmall
            : longDescription;
        if (!description)
            return null;
        const truncatedDoc = Object.assign(Object.assign({}, description), { content: (() => {
                const newContent = [];
                for (const node of description.content || []) {
                    newContent.push(node);
                    if (node.nodeType === BLOCKS.UL_LIST) {
                        break;
                    }
                }
                return newContent;
            })() });
        return documentToReactComponents(truncatedDoc, tileType === 'horizontal' ? horizontalOptions : options);
    }, [longDescription, tileType, longDescriptionSmall]);
    const isCurrentPlanType = useMemo(() => isCurrentPlan && (!isOnboarding || planType !== 'free'), [isCurrentPlan, isOnboarding, planType]);
    const PlanTileFeatures = useMemo(() => {
        return (_jsx(PlanTileBody, { "$tileType": tileType, children: formattedDescription }));
    }, [tileType, formattedDescription]);
    const onClick = useCallback(() => {
        handleClick(planType, duration);
    }, [handleClick, planType, duration]);
    const handleToggleCollapse = useCallback(() => {
        setIsCollapseOpen((current) => !current);
    }, []);
    const shouldShowFeatures = !!longDescription && (tileType !== 'collapse' || isCollapseOpen);
    const ctaButton = useMemo(() => {
        if (!planType)
            return null;
        return (_jsx(Button, { onClick: onClick, shape: "square", fullWidth: true, defaultStyle: planTheme.ctaColor, size: "medium", withArrowRight: !(isCurrentPlanType && !isOnboarding), children: isCurrentPlanType
                ? isOnboarding
                    ? t('onboardingClient|goToDashboard')
                    : t('checkoutFlow|currentPlanTag')
                : t(`onboardingClient|planSelection${stringToCamelCase(planType)}Cta`) }));
    }, [
        onClick,
        planTheme,
        isCurrentPlanType,
        isOnboarding,
        planType,
        t,
    ]);
    if (!planType)
        return null;
    return (_jsx(PlanTileWrapper, { "$tileType": tileType, children: _jsxs(PlanTileCard, { "$highlighted": highlighted, "$planType": planType, "$tileType": tileType, children: [!!badge && (_jsx(Div, { position: "absolute", fitContent: true, style: { transform: 'translateY(-50%)', right: theme.spacing.xl }, children: _jsx(HdTag, { text: badge, color: theme.colors.purple_5, withBorder: true, size: "large", style: { height: 'fit-content' } }) })), tileType === 'collapse' && (_jsx(Div, { position: "absolute", fitContent: true, onClick: handleToggleCollapse, style: {
                        top: theme.spacing.xxl,
                        right: theme.spacing.xl,
                        cursor: 'pointer',
                    }, children: _jsx(TabArrow, { width: theme.spacing.xl, height: theme.spacing.xl, down: isCollapseOpen }) })), _jsxs(PlanTileHeader, { "$highlighted": highlighted, "$tileType": tileType, children: [_jsxs(Div, { flex: "row", gap: theme.spacing.md, align: "center", children: [_jsx(Typography, { elementTheme: "h4", color: theme.colors.grey_2, children: planTitle }), !!(tag === null || tag === void 0 ? void 0 : tag.length) && (_jsx(HdTag, { text: tag, color: planTheme.tagColor, withBorder: true, size: "medium" })), isCurrentPlanType && (_jsx(HdTag, { text: t('checkoutFlow|currentPlanTag'), color: theme.colors.white_1, withBorder: true, size: "medium", style: { height: 'fit-content' } }))] }), !!pricings.length && planType === 'free' && (_jsx(Typography, { elementTheme: "body2", style: { paddingBlock: tileType === 'small' ? '2px' : '3px' }, children: pricings[0].fields.toggleLabel })), !!yearlyPrice && (_jsx(PlanTileToggle, { duration: duration, setDuration: setDuration, color: planTheme.ctaColor })), !!pricings.length && (_jsx(PricingLine, { duration: duration, monthlyPrice: planType === 'custom' ? pricings[0].fields.amount : monthlyPrice, yearlyPrice: yearlyPrice || '' })), (tileType === 'standard' || tileType === 'horizontal') && (_jsx(Typography, { elementTheme: "body2", children: shortDescription })), (tileType === 'horizontal' || tileType === 'collapse') && (_jsx(Div, { style: {
                                paddingTop: tileType === 'collapse' ? 0 : theme.spacing.lg,
                            }, children: ctaButton }))] }), shouldShowFeatures && PlanTileFeatures, (tileType === 'small' || tileType === 'standard') && (_jsx(PlanTileCta, { "$tileType": tileType, children: ctaButton }))] }) }));
};
export default PlanTile;
export { PlanTileGroup } from './styles';
